import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { SettingsState, UserPreferences, LocationSettings, ApiKeySettings } from '@/types/settings';
import { updateApiKeys } from '@/api-frontend/updateApiKeys';
import { updateLocationSettings } from '@/api-frontend/updateLocationSettings';
import { loadSettingsFromStorage } from '@/lib/AppSettings';

// Load initial state from localStorage if available
const loadInitialState = (): SettingsState => {
  const defaultState: SettingsState = {
    preferences: {
      conversationStyle: 'casual',
      topicsToAvoid: [],
      generationFrequency: 'medium',
    },
    location: {
      useCurrentLocation: true,
      permissionGranted: false,
    },
    apiKeys: {
      openWeatherMapKey: '',
      newsApiKey: '',
    },
    loading: false,
    error: null,
  };

  // Check for settings in app-settings first
  const storedSettings = loadSettingsFromStorage();
  let initialState = defaultState;
  
  if (storedSettings) {
    initialState = {
      ...defaultState,
      ...storedSettings,
      loading: false,
      error: null,
    };
  } else {
    // Fallback to setup-config for API keys if no settings exist
    try {
      const setupConfig = localStorage.getItem('setup-config');
      if (setupConfig) {
        const parsed = JSON.parse(setupConfig);
        if (parsed.apiKeys) {
          initialState.apiKeys = {
            openWeatherMapKey: parsed.apiKeys.openWeatherMapKey || '',
            newsApiKey: parsed.apiKeys.newsApiKey || '',
          };
        }
        if (parsed.locationPermissionGranted !== undefined) {
          initialState.location.permissionGranted = parsed.locationPermissionGranted;
        }
      }
    } catch (error) {
      console.warn('Failed to load setup config:', error);
    }
  }

  return initialState;
};

const initialState = loadInitialState();

// Async thunks
export const saveApiKeys = createAsyncThunk(
  'settings/saveApiKeys',
  async (apiKeys: ApiKeySettings, { dispatch }) => {
    const result = await updateApiKeys(apiKeys);
    
    // Also sync with setup slice for backward compatibility
    dispatch({ 
      type: 'setup/updateApiKeys', 
      payload: {
        openWeatherMapKey: apiKeys.openWeatherMapKey,
        newsApiKey: apiKeys.newsApiKey,
      }
    });
    
    return result;
  }
);

export const saveLocationSettings = createAsyncThunk(
  'settings/saveLocationSettings',
  async (location: LocationSettings) => {
    const result = await updateLocationSettings(location);
    return result;
  }
);

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    updatePreferences: (state, action: PayloadAction<Partial<UserPreferences>>) => {
      state.preferences = { ...state.preferences, ...action.payload };
      // Auto-save to localStorage
      try {
        localStorage.setItem('app-settings', JSON.stringify({
          preferences: state.preferences,
          location: state.location,
          apiKeys: state.apiKeys,
        }));
      } catch (error) {
        console.warn('Failed to auto-save settings:', error);
      }
    },
    updateLocationSettings: (state, action: PayloadAction<Partial<LocationSettings>>) => {
      state.location = { ...state.location, ...action.payload };
      // Auto-save to localStorage
      try {
        localStorage.setItem('app-settings', JSON.stringify({
          preferences: state.preferences,
          location: state.location,
          apiKeys: state.apiKeys,
        }));
      } catch (error) {
        console.warn('Failed to auto-save settings:', error);
      }
    },
    updateApiKeysLocal: (state, action: PayloadAction<Partial<ApiKeySettings>>) => {
      state.apiKeys = { ...state.apiKeys, ...action.payload };
      // Auto-save to localStorage
      try {
        localStorage.setItem('app-settings', JSON.stringify({
          preferences: state.preferences,
          location: state.location,
          apiKeys: state.apiKeys,
        }));
      } catch (error) {
        console.warn('Failed to auto-save settings:', error);
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    loadFromLocalStorage: (state) => {
      // Load settings from localStorage if available
      try {
        const stored = localStorage.getItem('app-settings');
        if (stored) {
          const parsed = JSON.parse(stored);
          return { ...state, ...parsed };
        }
      } catch (error) {
        console.warn('Failed to load settings from localStorage:', error);
      }
    },
    saveToLocalStorage: (state) => {
      try {
        localStorage.setItem('app-settings', JSON.stringify({
          preferences: state.preferences,
          location: state.location,
          apiKeys: state.apiKeys,
        }));
      } catch (error) {
        console.warn('Failed to save settings to localStorage:', error);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Save API Keys
      .addCase(saveApiKeys.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveApiKeys.fulfilled, (state, action) => {
        state.loading = false;
        state.apiKeys = action.payload;
      })
      .addCase(saveApiKeys.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to save API keys';
      })
      
      // Save Location Settings
      .addCase(saveLocationSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveLocationSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.location = action.payload;
      })
      .addCase(saveLocationSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to save location settings';
      });
  },
});

export const {
  updatePreferences,
  updateLocationSettings,
  updateApiKeysLocal,
  clearError,
  loadFromLocalStorage,
  saveToLocalStorage,
} = settingsSlice.actions;

export default settingsSlice.reducer;